export class ScopedValue {
    constructor() {
        this.value = 0;
        this.children = {};
    }
    /**
     * Register a ScopedValue. This may create a new ScopedValue object
     * or return an existing one under the same scope-path.
     *
     * @param scope a dot-separated path defining a scope hierarchy (e.g. session.level.shot_count)
     * @returns a ScopedValue object
     * @example
     * ```
     * let scopedCounter = ScopedValue.register('session.level.shot_count');
     * ```
     */
    static register(scope) {
        const path = scope.split('.');
        return this.makeItem(path);
    }
    /**
     * Lookup a ScopedValue object by its scope path, either returns a ScopedValue
     * object, or null (if not found).
     * @param scope a dot-separated path defining a scope hierarchy (e.g. session.level.shot_count)
     * @returns a ScopedValue object or null
     * @example
     * ```
     * let scopedCounter = ScopedValue.find('session.level.shot_count');
     * if (scopedCounter) {
     *   ...
     * }
     * ```
     */
    static find(scope) {
        const path = scope.split('.');
        return this.findItem(path);
    }
    /**
     * Discard the entire ScopedValue system, destroying the hierarchy of scoped values.
     * Usually this function shouldn't be called (but it's useful for running tests).
     */
    static discard() {
        ScopedValue.root = new ScopedValue();
    }
    /**
     * Recursively sets a ScopedValue and all its child objects to a value of zero.
     * @example
     * ```
     * let shotCount = ScopedValue.register('session.level.shot_count');
     * let bombCount = ScopedValue.register('session.level.bomb_count');
     *
     * // set all counters under 'session.level' to zero
     * ScopedValue.find('session.level')?.clear();
     * ```
     */
    clear() {
        this.value = 0;
        Object.keys(this.children).forEach((key) => {
            this.children[key].value = 0;
            this.children[key].clear();
        });
    }
    /**
     * Get the embedded value of this ScopedValue object.
     * @returns the current value of this object
     */
    get() {
        return this.value;
    }
    /**
     * Set an absolute value on the ScopedValue object.
     * @param value the value to set
     */
    set(value) {
        this.value = value;
    }
    /**
     * Bump the embedded value of this ScopedValue object by
     * an optional amount (omitting the amount param incrmeents by one).
     * @param amount optional increment amount (default: 1)
     */
    bump(amount = 1) {
        this.value += amount;
    }
    /**
     * Private helper function to find ScopedValue by its path.
     * @param path the scope path split into a string array
     * @returns found ScopedValue object, or null if not found.
     */
    static findItem(path) {
        let item = ScopedValue.root;
        for (let i = 0; i < path.length; i += 1) {
            const id = path[i];
            if (item.children[id] === undefined) {
                return null;
            }
            item = item.children[id];
        }
        return item;
    }
    /**
     * Private helper function to find or create a ScopedValue
     * by its path. Creates any 'missing' objects in the path.
     * @param path the scope path split into a string array
     * @returns an existing or new ScopedValue object
     */
    static makeItem(path) {
        let item = ScopedValue.root;
        for (let i = 0; i < path.length; i += 1) {
            const id = path[i];
            if (item.children[id] === undefined) {
                item.children[id] = new ScopedValue();
            }
            item = item.children[id];
        }
        return item;
    }
}
ScopedValue.root = new ScopedValue();
//# sourceMappingURL=scopedvalue.js.map